import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { YodiMark } from '@/components/brand/YodiMark';
import type { AuthShellCopy } from './auth-shell.copy';
import { formatFigure, useCountUp } from './use-auth-motion';
import { useAuthShellFigures, type AuthShellFigures as FiguresState } from './use-auth-shell-figures';

export function AuthPhotoPanel({ copy, lang }: { copy: AuthShellCopy; lang: string }) {
  const figures = useAuthShellFigures();

  return (
    <aside className="auth-panel hidden md:flex" data-testid="auth-photo-panel">
      <div className="auth-photo">
        <img src="/images/auth-community.jpg" alt="" width={1400} height={933} decoding="async" />
      </div>
      <div className="auth-veil" aria-hidden="true" />

      <Link
        to={`/${lang}`}
        className="absolute left-8 top-7 z-10 inline-flex items-center gap-3 rounded-xl text-lg font-semibold tracking-[-0.02em] text-white focus-ring"
      >
        <YodiMark className="size-9 text-sm" />
        <span>{copy.brand}</span>
      </Link>

      <WaveDoodle className="auth-wave absolute right-8 top-7 z-10 h-9 w-[4.5rem] text-primary" />

      <div className="auth-panel-content relative">
        <LiveCard copy={copy} figures={figures} lang={lang} />

        <h2 className="max-w-[15ch] text-[clamp(2.1rem,3.4vw,3.2rem)] font-bold leading-[1.02] tracking-[-0.035em] text-white">
          <SplitWords text={copy.headline} delay={0.15} />{' '}
          <span className="auth-serif relative inline-block whitespace-nowrap pr-1">
            <SplitWords text={copy.headlineSerif} delay={0.45} startIndex={copy.headline.split(/\s+/).length} />
            <DrawnUnderline className="auth-underline absolute -bottom-2 left-0 h-3 w-full text-primary" />
          </span>
        </h2>

        <p className="mt-6 max-w-[27rem] text-[0.98rem] leading-relaxed text-white/80">{copy.description}</p>

        <PanelFigures copy={copy} figures={figures} lang={lang} />
      </div>
    </aside>
  );
}

function LiveCard({ copy, figures, lang }: { copy: AuthShellCopy; figures: FiguresState; lang: string }) {
  const validated = useCountUp(figures.status === 'ready' ? figures.validated : null, 1400, 700);
  const share =
    figures.status === 'ready' && figures.recordings > 0
      ? Math.round((figures.validated / figures.recordings) * 100)
      : null;

  return (
    <div
      className="auth-card mb-8 inline-flex min-w-[15rem] max-w-full flex-col gap-2.5 rounded-2xl border border-white/15 bg-black/45 px-4 py-3.5 backdrop-blur-md"
      data-testid="auth-live-card"
    >
      <span className="flex items-center gap-2 whitespace-nowrap text-[0.68rem] font-semibold uppercase tracking-[0.12em] text-white/75">
        <span className="auth-live-dot size-1.5 rounded-full bg-primary" aria-hidden="true" />
        {figures.status === 'unavailable' ? (
          copy.liveFallbackTitle
        ) : (
          <>
            {copy.live}
            <span aria-hidden="true">·</span>
            {copy.liveValidated}
          </>
        )}
      </span>

      {figures.status === 'ready' ? (
        <>
          <span className="text-[1.75rem] font-bold leading-none tracking-[-0.03em] tabular-nums text-white">
            {formatFigure(validated ?? figures.validated, lang)}
          </span>
          {share !== null ? (
            <>
              <span className="auth-card-bar block h-1 overflow-hidden rounded-full bg-white/15" aria-hidden="true">
                <span className="block h-full rounded-full bg-primary" style={{ width: `${Math.min(100, share)}%` }} />
              </span>
              <span className="text-xs text-white/65">{copy.liveShare(share)}</span>
            </>
          ) : null}
        </>
      ) : figures.status === 'loading' ? (
        <span className="auth-skeleton block h-7 w-28 rounded-md bg-white/15" aria-hidden="true" />
      ) : (
        <span className="text-sm text-white/80">{copy.liveFallbackText}</span>
      )}
    </div>
  );
}

function PanelFigures({ copy, figures, lang }: { copy: AuthShellCopy; figures: FiguresState; lang: string }) {
  const ready = figures.status === 'ready' ? figures : null;
  const recordings = useCountUp(ready ? ready.recordings : null, 1800, 400);
  const contributors = useCountUp(ready ? ready.contributors : null, 1800, 500);
  const languages = useCountUp(ready ? ready.languages : null, 1200, 600);

  // No figures and no way to get them: say nothing rather than show zeros or dashes.
  if (figures.status === 'unavailable') return null;

  const items = [
    { key: 'recordings', label: copy.statRecordings, value: recordings },
    { key: 'contributors', label: copy.statContributors, value: contributors },
    { key: 'languages', label: copy.statLanguages, value: languages },
  ];

  return (
    <dl
      className="mt-8 grid grid-cols-3 gap-4 border-t border-white/15 pt-6 lg:gap-9"
      aria-label={copy.statsLabel}
      aria-busy={figures.status === 'loading'}
      data-testid="auth-figures"
    >
      {items.map((item) => (
        <div key={item.key} className="flex min-w-0 flex-col gap-1">
          <dt className="order-2 text-[0.8rem] leading-snug text-white/65">{item.label}</dt>
          <dd className="order-1 text-[clamp(1.25rem,1.9vw,1.6rem)] font-bold leading-none tracking-[-0.03em] tabular-nums text-white">
            {item.value === null ? (
              <span className="auth-skeleton block h-6 w-16 rounded-md bg-white/15">
                <span className="sr-only">{copy.loadingFigures}</span>
              </span>
            ) : (
              formatFigure(item.value, lang)
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** Words that rise one after the other behind a mask (CSS only). */
export function SplitWords({ text, delay = 0, startIndex = 0 }: { text: string; delay?: number; startIndex?: number }) {
  const words = text.split(/\s+/).filter(Boolean);
  return (
    <span>
      <span className="sr-only">{text}</span>
      {words.map((word, index) => (
        <Fragment key={`${word}-${index}`}>
          <span className="auth-mask" aria-hidden="true">
            <span
              className="auth-unit"
              style={{ ['--i' as string]: startIndex + index, ['--auth-delay' as string]: `${delay}s` }}
            >
              {word}
            </span>
          </span>
          {index < words.length - 1 ? ' ' : null}
        </Fragment>
      ))}
    </span>
  );
}

function DrawnUnderline({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 200 16"
      preserveAspectRatio="none"
      className={className}
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth={4}
      strokeLinecap="round"
    >
      <path pathLength={1} d="M4 11c38-6 92-9 192-4" />
    </svg>
  );
}

/** A hand-drawn voice waveform: uneven strokes, as if traced with a marker. */
function WaveDoodle({ className }: { className?: string }) {
  const bars = ['M6 18v4', 'M15 12.5v15', 'M24 7v26', 'M33 13v13.5', 'M42 9.5v21', 'M51 15v10', 'M60 17.5v5.5'];
  return (
    <svg
      viewBox="0 0 66 40"
      className={className}
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth={3.4}
      strokeLinecap="round"
    >
      {bars.map((d, index) => (
        <path key={d} d={d} pathLength={1} style={{ ['--i' as string]: index }} />
      ))}
    </svg>
  );
}
